import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StellarReplyWalkthrough from '../components/StellarReplyWalkthrough';
import { trackEvent } from '../services/analytics';
import { daysUntilAssessment } from '../utils/assessment';

const detectCurrency = () => {
  try {
    const locale = navigator.language || 'en-GB';
    if (locale.includes('US') || locale === 'en-US') return 'usd';
    return 'gbp';
  } catch {
    return 'gbp';
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [priceCurrency, setPriceCurrency] = useState(detectCurrency());

  const symbol = priceCurrency === 'usd' ? '$' : '£';
  // Live, and true without any invented statistic: the quarterly Superhost date.
  const daysToAssessment = daysUntilAssessment();

  // Inject JSON-LD schema markup for SEO
  useEffect(() => {
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'stellarreply-schema';
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "StellarReply",
      "url": "https://stellarreply.com",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-powered review response platform for local businesses and Airbnb hosts. Drafts personalised replies to Google, Airbnb and Trustpilot reviews in seconds.",
      "offers": {
        "@type": "Offer",
        "price": "20.00",
        "priceCurrency": "GBP",
        "priceValidUntil": "2026-12-31"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "1"
      },
      "creator": {
        "@type": "Organization",
        "name": "OpsLifeUK Limited",
        "url": "https://stellarreply.com"
      }
    });
    document.head.appendChild(schema);
    return () => {
      const el = document.getElementById('stellarreply-schema');
      if (el) el.remove();
    };
  }, []);

  // Track landing page visit
  useEffect(() => { trackEvent('landing_visit'); }, []);

  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">Stellar<span>Reply</span></div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a
              className="landing-btn-secondary"
              href="/airbnb-superhost-checker"
              onClick={(e) => { e.preventDefault(); trackEvent('cta_free_tool', { location: 'nav', tool: 'superhost_checker' }); navigate('/airbnb-superhost-checker'); }}
              style={{ padding: '8px 16px', fontSize: '14px', textDecoration: 'none' }}
            >
              Check your Superhost standing
            </a>
            <button className="landing-nav-cta" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-badge">Know where you stand</div>
          <h1 className="landing-h1">
            Know exactly where you stand.<br />
            <span className="landing-h1-accent">Then close the gap.</span>
          </h1>
          <p className="landing-hero-sub">
            StellarReply works out the precise number of 5-star reviews between you and your
            target. That is 4.8 for Airbnb Superhost, or whatever keeps you visible on Google.
            You also see how many bad reviews you can absorb before you slip, and it drafts the
            replies that protect the number.
          </p>
          <div className="landing-hero-actions">
            <button className="landing-btn-primary" onClick={() => { trackEvent('cta_start_trial', { location: 'hero' }); navigate('/login?register=true'); }}>
              Start free trial
            </button>
            <button className="landing-btn-secondary" onClick={() => {
              document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              See how it works
            </button>
          </div>
          <p className="landing-hero-note">
            14-day free trial &middot; No credit card required &middot;{' '}
            <a
              href="/airbnb-rating-calculator"
              onClick={() => trackEvent('cta_free_tool', { location: 'hero', tool: 'calculator' })}
              style={{ textDecoration: 'underline', cursor: 'pointer', color: '#34d399' }}
            >
              Or work out your standing free
            </a>
          </p>
          <p className="landing-hero-note" style={{ marginTop: 8 }}>
            &#127968; Airbnb or holiday-let host?{' '}
            <span
              onClick={() => navigate('/for-airbnb-hosts')}
              style={{ textDecoration: 'underline', cursor: 'pointer', color: '#34d399', fontWeight: 600 }}
            >
              See StellarReply for hosts &rarr;
            </span>
          </p>
        </div>

        {/* Floating review cards */}
        <div className="landing-hero-visual">
          <div className="landing-review-card landing-review-1">
            <div className="landing-review-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            <p>"Spotless flat and the local tips were perfect. Would stay again!"</p>
            <span className="landing-review-author">Sarah M.</span>
          </div>
          <div className="landing-reply-card landing-reply-1">
            <div className="landing-reply-label">&#10022; AI Draft <span style={{ fontSize: '10px', color: '#9333ea', marginLeft: '6px', fontWeight: 600 }}>&#9889; Smart Insight</span></div>
            <p>"Thank you, Sarah! So glad the cafe tips worked out. You were lovely guests and you're welcome back any time."</p>
          </div>
          <div className="landing-review-card landing-review-2">
            <div className="landing-review-stars">&#9733;&#9733;&#9733;&#9734;&#9734;</div>
            <p>"Nice flat, but check-in was confusing and we waited outside."</p>
            <span className="landing-review-author">James T.</span>
          </div>
          <div className="landing-reply-card landing-reply-2">
            <div className="landing-reply-label">&#10022; AI Draft</div>
            <p>"Sorry about the wait, James. We've added key-box photos to the arrival guide so the next guests walk straight in."</p>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="landing-proof">
        <div className="landing-proof-inner">
          <div className="landing-proof-item">
            <strong>871,667</strong>
            <span>Airbnb listings in our Superhost study</span>
          </div>
          <div className="landing-proof-divider" />
          <div className="landing-proof-item">
            <strong>{daysToAssessment}</strong>
            <span>
              {daysToAssessment === 1 ? 'Day' : 'Days'} until the next Superhost assessment
            </span>
          </div>
          <div className="landing-proof-divider" />
          <div className="landing-proof-item">
            <strong>100%</strong>
            <span>You approve every reply</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="landing-section" id="how-it-works">
        <div className="landing-section-inner">
          <div className="landing-section-label">How it works</div>
          <h2 className="landing-h2">Three steps to a rating you control</h2>
          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-num">01</div>
              <h3>Know where you stand</h3>
              <p>Enter your rating and review count, or import your reviews, and see the exact number of 5-star reviews between you and your target. That is 4.8 for Superhost, or whatever keeps you visible on Google. You also see how many bad reviews you can absorb before you slip.</p>
            </div>
            <div className="landing-step-arrow">&rarr;</div>
            <div className="landing-step">
              <div className="landing-step-num">02</div>
              <h3>Stop the slide</h3>
              <p>Most bad reviews start as a problem nobody fixed in time. The Review Recovery Loop helps you put things right with an unhappy guest or customer before it becomes a public 1-star, and if a review breaks the platform's rules, we tell you whether it can be removed.</p>
            </div>
            <div className="landing-step-arrow">&rarr;</div>
            <div className="landing-step">
              <div className="landing-step-num">03</div>
              <h3>Close the gap</h3>
              <p>AI drafts a reply to every review in your voice, and every one is your call to approve, edit, or hand to Autopilot. Branded review links and QR codes bring in the new 5-stars that actually move the number.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive product walkthrough */}
      <section id="product-demo" style={{
        background: '#ffffff',
        padding: '60px 20px',
        borderTop: '1px solid #e8e5df',
        borderBottom: '1px solid #e8e5df',
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <div className="landing-section-label">See it in action</div>
          <h2 className="landing-h2" style={{ marginBottom: '32px' }}>Watch how StellarReply works</h2>
          <StellarReplyWalkthrough />
        </div>
      </section>

      {/* Getting Started */}
      <section className="landing-section" id="getting-started">
        <div className="landing-section-inner">
          <div className="landing-section-label">Getting started</div>
          <h2 className="landing-h2">Up and running in under 5 minutes</h2>
          <p style={{
            textAlign: 'center', fontSize: '15px', color: 'rgba(0,0,0,0.55)',
            maxWidth: '520px', margin: '-10px auto 36px', lineHeight: '1.7',
          }}>
            You get your standing the moment you sign in, with no setup needed. These three steps then unlock competitor tracking, automatic review import and the recovery tools.
          </p>
          <div style={{
            maxWidth: '640px', margin: '0 auto',
            display: 'flex', flexDirection: 'column', gap: '0',
          }}>
            {[
              {
                num: '1',
                title: 'Add your listing or business',
                desc: 'Airbnb host? Add your rating and review count, or paste your guest reviews, and see your exact distance from 4.8. Local business? Paste your Google Maps URL instead, which also creates your branded review link and QR code.',
                time: '1 min',
                icon: '&#9881;',
              },
              {
                num: '2',
                title: 'Add your competitors',
                desc: 'Paste up to 5 nearby Airbnb listings or Google Maps businesses. StellarReply compares your ratings with theirs and mines their weak spots so your listing and replies stand out.',
                time: '2 min',
                icon: '&#128200;',
              },
              {
                num: '3',
                title: 'Add your first reviews',
                desc: 'Paste in your latest Airbnb guest reviews (or use "Sync from Google" for a Google business). The AI drafts a reply to each one in your voice, for you to approve.',
                time: '2 min',
                icon: '&#9889;',
              },
            ].map((step, i) => (
              <div key={i} style={{
                display: 'flex', gap: '20px', padding: '24px 0',
                borderBottom: i < 2 ? '1px solid #e8e5df' : 'none',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #16a34a, #059669)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 18, fontWeight: 800, flexShrink: 0,
                }}>
                  {step.num}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>{step.title}</h3>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: '#16a34a',
                      background: 'rgba(22,163,74,0.08)', padding: '2px 8px', borderRadius: '8px',
                    }}>
                      {step.time}
                    </span>
                  </div>
                  <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.55)', margin: 0, lineHeight: '1.6' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button className="landing-btn-primary" onClick={() => { trackEvent('cta_start_trial', { location: 'getting_started' }); navigate('/login?register=true'); }}>
              Start free trial
            </button>
            <p style={{ fontSize: '12px', color: 'rgba(0,0,0,0.35)', marginTop: '10px' }}>
              Want the full walkthrough? Read our{' '}
              <a href="/blog/getting-started-with-stellarreply.html" style={{ color: '#16a34a', textDecoration: 'underline' }}>
                step-by-step getting started guide
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Free Review Analysis CTA */}
      <section className="landing-section" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        <div className="landing-section-inner" style={{ textAlign: 'center' }}>
          <h2 className="landing-h2" style={{ fontSize: '24px' }}>
            Where does your listing stand right now?
          </h2>
          <p style={{
            fontSize: '15px', color: 'rgba(0,0,0,0.55)', maxWidth: '480px',
            margin: '-8px auto 24px', lineHeight: '1.6',
          }}>
            Paste your Airbnb listing link and see exactly how far you are from 4.8, how many bad reviews you can absorb, and when the next Superhost assessment is. Free, no signup.
          </p>
          <button className="landing-btn-primary" onClick={() => { trackEvent('cta_free_tool', { location: 'mid', tool: 'superhost_checker' }); navigate('/airbnb-superhost-checker'); }}
            style={{ padding: '14px 32px', fontSize: '16px' }}>
            Check my Superhost standing free
          </button>
          <p style={{ fontSize: '13px', color: 'rgba(0,0,0,0.5)', marginTop: '12px' }}>
            Run a local business?{' '}
            <a href="/audit" onClick={() => trackEvent('cta_free_audit')} style={{ color: '#16a34a', textDecoration: 'underline' }}>
              Check your Google review score instead
            </a>
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="landing-section landing-section-dark">
        <div className="landing-section-inner">
          <div className="landing-section-label">Features</div>
          <h2 className="landing-h2">Everything you need to move the number</h2>
          <div className="landing-features">
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128737;</div>
              <h3>Smart Feedback Funnel</h3>
              <p>Share one link. Happy customers go straight to Google. Unhappy ones send you private feedback first, so you can fix it before it goes public. The most direct way to protect the rating you already have.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128260;</div>
              <h3>Review Recovery Loop</h3>
              <p>Intercept unhappy customers before they post publicly. AI drafts a personalised recovery message, you make things right, then convert them into 5-star advocates. No other tool does this.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#9889;</div>
              <h3>Instant AI drafts</h3>
              <p>Powered by advanced AI that understands context, sentiment, and your brand voice. Every draft feels like you wrote it, and answered reviews are what keep the next guest reading past the bad one.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128161;</div>
              <h3>Competitor Weakness Mining</h3>
              <p>We read your competitors' bad reviews so you don't have to. Our AI spots their recurring weaknesses and subtly highlights your strengths in every response you send.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#127761;</div>
              <h3>Review translation</h3>
              <p>Translate reviews into any language with one click. Understand every piece of customer feedback, no matter the language.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#127942;</div>
              <h3>Competitor benchmarking</h3>
              <p>Track up to 5 competitors. Compare ratings, review volume, and trends. AI analyses both sides and tells you exactly where to improve and where you're winning.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128187;</div>
              <h3>Embeddable review wall</h3>
              <p>Showcase your best reviews on your website with one line of code. Auto-updates as new reviews come in. Three layouts to match your site.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128214;</div>
              <h3>Listing Intelligence Engine</h3>
              <p>For Airbnb hosts: AI reads your guest reviews and writes an optimised listing description using your guests' own words. Plus title suggestions, amenity recommendations, and a pricing confidence signal.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#9997;</div>
              <h3>Guest Review Writer</h3>
              <p>Rate your guest on cleanliness, communication, and house rules. AI writes a thoughtful, specific host-to-guest review in seconds. No more staring at a blank text box after checkout.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128202;</div>
              <h3>Dashboard &amp; analytics</h3>
              <p>Track your reviews, response rates, and ratings at a glance. AI-powered insights tell you what customers love and what needs work.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">📈</div>
              <h3>Reputation Forecast</h3>
              <p>See where your rating is heading 30, 60, and 90 days from now. Toggle "what if" scenarios to see how fixing complaints or boosting review volume changes your trajectory. Like a weather forecast for your reputation.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#127912;</div>
              <h3>Shareable review cards</h3>
              <p>Turn your best reviews into beautiful branded images. Share on social media, print them in store, or add them to marketing materials.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#9998;</div>
              <h3>Always in your control</h3>
              <p>Every AI response is a draft, not a final answer. Approve it instantly, edit the wording, or regenerate until it's perfect.</p>
            </div>
            <div className="landing-feature">
              <div className="landing-feature-icon">&#128274;</div>
              <h3>Secure &amp; private</h3>
              <p>Your data is encrypted, stored securely on AWS, and never shared. GDPR-compliant by design.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Competitor comparison */}
      <section className="landing-section">
        <div className="landing-section-inner">
          <div className="landing-section-label">Why StellarReply</div>
          <h2 className="landing-h2">Premium features at a fraction of the price</h2>
          <p style={{
            textAlign: 'center',
            fontSize: '15px',
            color: 'rgba(0,0,0,0.55)',
            maxWidth: '600px',
            margin: '-10px auto 36px',
            lineHeight: '1.7',
          }}>
            Features that reputation management platforms charge hundreds per month for, included in every StellarReply plan.
          </p>
          <div style={{
            maxWidth: '700px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: '0',
            fontSize: '14px',
          }}>
            {/* Header row */}
            <div style={{ padding: '12px 16px', fontWeight: 700, borderBottom: '2px solid #e8e5df' }}>Feature</div>
            <div style={{ padding: '12px 20px', fontWeight: 700, borderBottom: '2px solid #e8e5df', textAlign: 'center', color: '#16a34a' }}>StellarReply</div>
            <div style={{ padding: '12px 20px', fontWeight: 700, borderBottom: '2px solid #e8e5df', textAlign: 'center', color: 'rgba(0,0,0,0.4)' }}>Others</div>

            {[
              ['AI review responses', true, '$50-100/mo'],
              ['Review Recovery Loop', true, 'Not available'],
              ['Competitor Weakness Mining', true, 'Not available'],
              ['Smart Feedback Funnel', true, '$200+/mo'],
              ['Competitor benchmarking', true, '$100-200/mo'],
              ['Embeddable review widget', true, '$20-50/mo'],
              ['Free review analysis', true, 'Not available'],
              ['Review translation', true, '$30+/mo'],
              ['Shareable review cards', true, 'Not available'],
              ['Listing Intelligence (Airbnb)', true, 'Not available'],
              ['AI Guest Review Writer', true, 'Not available'],
              ['Bulk review import', true, 'Not available'],
              ['Analytics dashboard', true, 'Included'],
              ['Reputation Forecast & What-If', true, 'Not available'],
            ].map(([feature, included, others], i) => (
              <div key={i} style={{ display: 'contents' }}>
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0ede8',
                  color: 'rgba(0,0,0,0.7)',
                }}>{feature}</div>
                <div style={{
                  padding: '12px 20px',
                  borderBottom: '1px solid #f0ede8',
                  textAlign: 'center',
                  color: '#16a34a',
                  fontWeight: 600,
                }}>&#10003; Included</div>
                <div style={{
                  padding: '12px 20px',
                  borderBottom: '1px solid #f0ede8',
                  textAlign: 'center',
                  color: others === 'Not available' ? '#dc2626' : 'rgba(0,0,0,0.35)',
                  fontSize: '13px',
                  fontWeight: others === 'Not available' ? 600 : 400,
                }}>{others}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="landing-section landing-section-dark">
        <div className="landing-section-inner">
          <div className="landing-section-label">Built for local businesses &amp; Airbnb hosts</div>
          <h2 className="landing-h2">Perfect for businesses that live and die by reviews</h2>
          <div className="landing-usecases">
            <div className="landing-usecase">&#127829; Restaurants &amp; cafes</div>
            <div className="landing-usecase">&#128135; Salons &amp; barbers</div>
            <div className="landing-usecase">&#129463; Dental practices</div>
            <div className="landing-usecase">&#127947; Gyms &amp; fitness studios</div>
            <div className="landing-usecase">&#128295; Tradespeople</div>
            <div className="landing-usecase">&#127976; Hotels &amp; B&amp;Bs</div>
            <div className="landing-usecase">&#128062; Vets &amp; pet services</div>
            <div className="landing-usecase">&#127968; Airbnb hosts</div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="landing-section" id="pricing">
        <div className="landing-section-inner">
          <div className="landing-section-label">Simple pricing</div>
          <h2 className="landing-h2">One plan. Everything included.</h2>
          <div className="landing-pricing">
            <div className="landing-price-card">
              <div className="landing-early-bird">Early adopter pricing - flat price, no credits, no per-response charges</div>
              <div className="landing-price-name">StellarReply Pro</div>

              <div className="landing-price-amount">
                {symbol}20<span>/month</span>
              </div>
              <p style={{
                fontSize: '13px',
                textAlign: 'center',
                marginTop: '-8px',
                marginBottom: '16px',
                color: 'rgba(0,0,0,0.35)',
              }}>
                {priceCurrency === 'gbp' ? (
                  <>Prices in GBP &middot; <span onClick={() => setPriceCurrency('usd')} style={{ cursor: 'pointer', textDecoration: 'underline', color: 'rgba(0,0,0,0.5)' }}>Show in USD</span></>
                ) : (
                  <>Prices in USD &middot; <span onClick={() => setPriceCurrency('gbp')} style={{ cursor: 'pointer', textDecoration: 'underline', color: 'rgba(0,0,0,0.5)' }}>Show in GBP</span></>
                )}
              </p>

              <ul className="landing-price-features">
                <li><strong>Your exact standing:</strong> the 5-star reviews you need to reach 4.8 (Superhost) or 4.9</li>
                <li><strong>Your buffer:</strong> how many bad reviews you can absorb before you slip below the line</li>
                <li>Countdown to the next quarterly Superhost assessment</li>
                <li>Unlimited AI-drafted responses</li>
                <li>Google, Airbnb &amp; Trustpilot support</li>
                <li>Review Recovery Loop</li>
                <li>Competitor Weakness Mining with Smart Insights</li>
                <li>Smart Feedback Funnel</li>
                <li>Competitor benchmarking with AI analysis</li>
                <li>Embeddable review widget</li>
                <li>Free review analysis</li>
                <li>Review translation</li>
                <li>Shareable review cards</li>
                <li>Listing Intelligence Engine (Airbnb hosts)</li>
                <li>AI Guest Review Writer (Airbnb hosts)</li>
                <li>Bulk review import</li>
                <li>Analytics dashboard &amp; insights</li>
                <li>Reputation Forecast with What-If scenarios</li>
                <li>Branded review request links &amp; QR codes</li>
                <li>Priority support</li>
              </ul>
              <button className="landing-btn-primary" onClick={() => { trackEvent('cta_start_trial', { location: 'pricing' }); navigate('/login?register=true'); }} style={{ width: '100%' }}>
                Start 14-day free trial
              </button>
              <p className="landing-price-note">No credit card required to start &middot; Price locked in forever</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-cta">
        <div className="landing-section-inner">
          <h2 className="landing-h2">You are a specific number of reviews<br />from where you want to be.</h2>
          <p className="landing-cta-sub">
            StellarReply works out exactly how many, helps you protect the rating you already have,
            and drafts the replies that get you there. In your voice, always your call.
          </p>
          <button className="landing-btn-primary landing-btn-lg" onClick={() => { trackEvent('cta_start_trial', { location: 'final' }); navigate('/login?register=true'); }}>
            Start your free trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <div className="landing-logo">Stellar<span>Reply</span></div>
            <p>Know where you stand. Then close the gap.</p>
            <a
              href="https://www.saashub.com/stellarreply?utm_source=badge&utm_campaign=badge&utm_content=stellarreply&badge_variant=color&badge_kind=approved"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: '12px' }}
            >
              <img
                src="https://cdn-b.saashub.com/img/badges/approved-color.png?v=1"
                alt="StellarReply on SaaSHub"
                style={{ maxWidth: '120px', height: 'auto' }}
                loading="lazy"
              />
            </a>
          </div>
          <div className="landing-footer-links">
            <a href="/free-review-response-generator">Free Reply Generator</a>
            <a href="/for-airbnb-hosts">For Airbnb Hosts</a>
            <a href="/free-airbnb-review-response-generator">Airbnb Reply Generator</a>
            <a href="/free-airbnb-listing-audit">Airbnb Listing Audit</a>
            <a href="/airbnb-superhost-checker">Superhost Checker</a>
            <a href="/blog/airbnb-superhost-requirements.html">Superhost Requirements</a>
            <a href="/airbnb-rating-calculator">Airbnb Rating Calculator</a>
            <a href="/airbnb-rating-benchmark">Airbnb Rating Benchmark</a>
            <a href="/airbnb-superhost-assessment-dates">Superhost Assessment Dates</a>
            <a href="/airbnb-review-deadline">Airbnb Review Deadline</a>
            <a href="/google-rating-calculator">Google Rating Calculator</a>
            <a href="/competitor-review-calculator">Competitor Review Calculator</a>
            <a href="/remove-unfair-review">Remove an Unfair Review</a>
            <a href="/audit">Check Your Google Rating</a>
            <a href="/blog/index.html" target="_blank" rel="noopener noreferrer">Blog</a>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            {/* Show the address: a bare "Contact" mailto does nothing without a mail app */}
            <a href="mailto:admin@stellarreply.com">Contact: admin@stellarreply.com</a>
          </div>
          <div className="landing-footer-copy">
            &copy; 2026 StellarReply. Powered by OpsLifeUK Limited.
          </div>
        </div>
      </footer>
    </div>
  );
}
